import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('cyber-panel p-5', className)} {...props} />;
}

interface KpiCardProps {
  label: string;
  value: ReactNode;
  delta?: { value: string; positive?: boolean };
  icon?: ReactNode;
  accent?: 'cyan' | 'magenta' | 'green' | 'yellow' | 'purple' | 'orange';
}

export function KpiCard({ label, value, delta, icon, accent = 'cyan' }: KpiCardProps) {
  const accentClass = {
    cyan: 'text-neon-cyan border-neon-cyan/30',
    magenta: 'text-neon-magenta border-neon-magenta/30',
    green: 'text-neon-green border-neon-green/30',
    yellow: 'text-neon-yellow border-neon-yellow/30',
    purple: 'text-neon-purple border-neon-purple/30',
    orange: 'text-neon-orange border-neon-orange/30',
  }[accent];

  return (
    <div className="kpi-card group">
      <div className="flex items-start justify-between mb-3">
        <span className="text-[10px] font-display uppercase tracking-wider text-text-secondary">
          {label}
        </span>
        {icon && (
          <div
            className={cn(
              'w-8 h-8 rounded flex items-center justify-center border bg-bg-tertiary',
              accentClass,
            )}
          >
            {icon}
          </div>
        )}
      </div>
      <div className={cn('text-3xl font-display font-bold tabular-nums numeric', accentClass)}>
        {value}
      </div>
      {delta && (
        <div
          className={cn(
            'mt-2 text-xs font-mono',
            delta.positive ? 'text-neon-green' : 'text-neon-red',
          )}
        >
          {delta.positive ? '↑' : '↓'} {delta.value}
        </div>
      )}
    </div>
  );
}
